import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { apiAgent } from '../../../axiosConfig'
import { Loader } from '../../../design_system/components/Loader.jsx'
import { useToast } from '../../../design_system/components/Toast/ToastContext.jsx'
import { updateCurrentTeam } from '../../../redux/currentTeamSlice'
import { useSettingsContext, settingsSnapshot } from './SettingsContext'
import useCheckUserAccess from '../../custom_hooks/useCheckUserAccess'
import { ControlToggleRow } from './controls_tab/ControlsRows.jsx'
import DataSourcesSection from './controls_tab/DataSourcesSection.jsx'
import DirectorySection from './controls_tab/DirectorySection.jsx'
import { TOGGLE_FIELDS, SECTIONS, getFieldById, getFieldsBySection } from './controls_tab/controlsFields.js'

// ── Main component ────────────────────────────────────────────────────────────

const ControlsTab = () => {
    const current_team     = useSelector(s => s.current_team.team)
    const current_user     = useSelector(s => s.current_user.user)
    const dispatch         = useDispatch()
    const toast            = useToast()
    const checkUserAccess  = useCheckUserAccess()
    const { registerTab, unregisterTab, setHasChanges } = useSettingsContext()
    const canEditControls  = !!current_user?.is_admin

    const [values, setValues]                 = useState({})
    const [isDataLoaded, setIsDataLoaded]     = useState(false)
    const [lnAliasesUrns, setLnAliasesUrns]   = useState('')
    const [initialJobDays, setInitialJobDays] = useState('')
    const [initialJobDaysLocked, setInitialJobDaysLocked] = useState(false)
    const snapshotRef = useRef(null)
    const lnSnapshotRef = useRef('')
    const jobDaysSnapshotRef = useRef('')

    // Refs for latest values — used in handleSubmit to avoid stale closures
    const valuesRef = useRef(values)
    valuesRef.current = values
    const lnRef = useRef(lnAliasesUrns)
    lnRef.current = lnAliasesUrns
    const jobDaysRef = useRef(initialJobDays)
    jobDaysRef.current = initialJobDays

    // ── Load ─────────────────────────────────────────────────────────────────

    useEffect(() => {
        apiAgent({ method: 'get', url: '/team/team_details', params: {} })
            .then(res => {
                const d = res.data.data
                const df = d.displayable_features || {}
                const ts = d.team_settings || {}
                const loaded = {
                    // tracking
                    google_news_tracking:      d.google_news_tracking || false,
                    nlp_enabled:               d.nlp_enabled || false,
                    // newsfeed
                    display_mid_confidence:    df.display_mid_confidence || false,
                    newsfeed_enabled:          d.newsfeed_enabled || false,
                    display_mapper_suggestions: df.display_mapper_suggestions || false,
                    display_job_updates:       df.display_job_updates || false,
                    enable_job_updates_in_feed: d.enable_job_updates_in_feed || false,
                    // sec
                    sec_filings_enabled:       d.sec_filings_enabled || false,
                    sec_filings_premium:       d.sec_filings_premium || false,
                    // directory
                    display_new_directory:         df.display_new_directory || false,
                    display_ai_reports:            df.display_ai_reports || false,
                    display_directory_download:   df.display_directory_download || false,
                    display_only_mapped_data:     df.display_only_mapped_data || false,
                    // email
                    display_external_id:            df.display_external_id || false,
                    // export
                    display_linkedin_url_in_export: df.display_linkedin_url_in_export || false,
                    ln_url_in_directory_export:     df.ln_url_in_directory_export || false,
                    // crm
                    display_save_news_in_crm: df.display_save_news_in_crm || false,
                    display_save_jobs_in_crm: df.display_save_jobs_in_crm || false,
                    // security
                    two_fa_enabled:           ts.two_fa_enabled || false,
                }
                setValues(loaded)
                snapshotRef.current = settingsSnapshot(loaded)

                const lnVal = d.ln_aliases_urns || ''
                setLnAliasesUrns(lnVal)
                lnSnapshotRef.current = lnVal

                const jobVal = ts.initial_job_update_duration || ''
                setInitialJobDays(jobVal)
                jobDaysSnapshotRef.current = String(jobVal)
                setInitialJobDaysLocked(!!ts.initial_job_update_duration)
                setIsDataLoaded(true)
            })
            .catch(() => {})
    }, [])

    // ── Change detection ──────────────────────────────────────────────────────

    useEffect(() => {
        if (snapshotRef.current === null) return
        const togglesChanged = settingsSnapshot(values) !== snapshotRef.current
        const lnChanged = lnAliasesUrns !== lnSnapshotRef.current
        const jobDaysChanged = String(initialJobDays) !== jobDaysSnapshotRef.current
        setHasChanges(togglesChanged || lnChanged || jobDaysChanged)
    }, [values, lnAliasesUrns, initialJobDays, setHasChanges])

    // ── Value getter ─────────────────────────────────────────────────────────

    const getValue = (field) => !!values[field.key]

    // ── Toggle handler (local state only, no API call) ───────────────────────

    const handleToggleClick = (fieldId) => {
        if (!canEditControls) return
        const field = getFieldById(fieldId)
        if (field?.disabled) return
        setValues(prev => {
            const next = { ...prev, [field.key]: !prev[field.key] }
            // Turning a parent off resets its child toggles to their saved values.
            // Children marked offWithParent (e.g. SEC Premium) are switched off instead.
            if (prev[field.key]) {
                const original = snapshotRef.current ? JSON.parse(snapshotRef.current) : {}
                TOGGLE_FIELDS
                    .filter(f => f.parentKey === field.key)
                    .forEach(child => { next[child.key] = child.offWithParent ? false : (original[child.key] || false) })
            }
            return next
        })
    }

    // ── Visibility ───────────────────────────────────────────────────────────

    const isFieldVisible = (field) => {
        if (field.access && !checkUserAccess(field.access)) return false
        if (field.parentKey && !values[field.parentKey]) return false
        if (field.section === 'crm' && !current_team?.save_to_crm_enabled) return false
        return true
    }

    const isSectionVisible = (sectionKey) =>
        getFieldsBySection(sectionKey, isFieldVisible).length > 0

    // ── Save: build payload from all changed fields ──────────────────────────

    const handleSubmit = useCallback(() => {
        if (!canEditControls || !snapshotRef.current) return Promise.resolve()

        const currentValues = valuesRef.current
        const currentLn = lnRef.current
        const currentJobDays = jobDaysRef.current
        const original = JSON.parse(snapshotRef.current)
        const data = {}
        const featureUpdates = {}
        const teamSettingsUpdates = {}

        // Diff toggle values
        for (const field of TOGGLE_FIELDS) {
            if (currentValues[field.key] === original[field.key]) continue
            if (field.type === 'feature') {
                featureUpdates[field.key] = currentValues[field.key]
            } else if (field.type === 'team_settings') {
                teamSettingsUpdates[field.key] = currentValues[field.key]
            } else {
                data[field.key] = currentValues[field.key]
            }
        }

        if (Object.keys(featureUpdates).length > 0) {
            // Send ALL feature values (not just changed ones) because the
            // backend replaces the entire displayable_features object.
            const allFeatures = {}
            for (const field of TOGGLE_FIELDS) {
                if (field.type === 'feature') allFeatures[field.key] = currentValues[field.key]
            }
            data.displayable_features = allFeatures
        }
        if (Object.keys(teamSettingsUpdates).length > 0) {
            data.team_settings = { ...data.team_settings, ...teamSettingsUpdates }
        }

        // Sub-settings: ln_aliases_urns
        if (currentLn !== lnSnapshotRef.current) {
            data.ln_aliases_urns = currentLn
        }

        // Sub-settings: initial_job_update_duration (only if job updates toggled ON)
        if (String(currentJobDays) !== jobDaysSnapshotRef.current && currentValues.enable_job_updates_in_feed) {
            data.team_settings = { ...data.team_settings, initial_job_update_duration: parseInt(currentJobDays, 10) || null }
        }

        if (Object.keys(data).length === 0) return Promise.resolve()

        return apiAgent({ method: 'post', url: '/team/update_controls', data })
            .then(res => {
                dispatch(updateCurrentTeam(res.data.team))
                toast.success('Controls saved')
                // Update snapshots from refs (always latest)
                snapshotRef.current = settingsSnapshot(currentValues)
                lnSnapshotRef.current = currentLn
                jobDaysSnapshotRef.current = String(currentJobDays)
                if (currentJobDays && currentValues.enable_job_updates_in_feed) setInitialJobDaysLocked(true)
                setHasChanges(false)
            })
            .catch(() => toast.error('Something went wrong. Please try again.'))
    }, [canEditControls, dispatch, toast, setHasChanges]) // no values/lnAliasesUrns/initialJobDays — reads from refs

    const handleCancel = useCallback(() => {
        if (snapshotRef.current) setValues(JSON.parse(snapshotRef.current))
        setLnAliasesUrns(lnSnapshotRef.current)
        setInitialJobDays(jobDaysSnapshotRef.current)
        // Reset locked state based on snapshot (was it locked when we loaded?)
        setInitialJobDaysLocked(!!jobDaysSnapshotRef.current && jobDaysSnapshotRef.current !== '')
    }, [])

    // ── Register with settings footer ────────────────────────────────────────

    const submitRef = useRef(handleSubmit)
    const cancelCallbackRef = useRef(handleCancel)
    submitRef.current = handleSubmit
    cancelCallbackRef.current = handleCancel

    useEffect(() => {
        const id = registerTab({
            onSave: (...args) => submitRef.current(...args),
            onCancel: (...args) => cancelCallbackRef.current(...args),
        })
        return () => unregisterTab(id)
    }, [registerTab, unregisterTab])

    // ── Render ────────────────────────────────────────────────────────────────

    const renderGenericField = (field) => (
        <ControlToggleRow
            key={field.id}
            label={field.label}
            description={field.description}
            checked={getValue(field)}
            disabled={field.disabled || !canEditControls}
            nested={!!field.parentKey}
            onToggleClick={() => handleToggleClick(field.id)}
        />
    )

    return (
        <div className="settings-panel">
            <div className="settings-panel__header">
                <span className="body-l-bold settings-panel__title">Controls</span>
                <span className="body-m settings-panel__description">
                    Turn features on or off to control what your team can access and do.
                </span>
            </div>

            {!isDataLoaded ? <Loader isLoading Size="Medium" /> : <>

            {SECTIONS.filter(s => isSectionVisible(s.key)).map(section => (
                <div key={section.key} className="settings-panel__section">
                    <span className="body-m-bold settings-panel__section-title">{section.label}</span>

                    {!['data_sources', 'directory'].includes(section.key) && getFieldsBySection(section.key, isFieldVisible).map(renderGenericField)}

                    {section.key === 'data_sources' && (
                        <DataSourcesSection
                            currentTeam={current_team}
                            values={values}
                            originalValues={snapshotRef.current ? JSON.parse(snapshotRef.current) : {}}
                            initialJobUpdate={{ days: initialJobDays, locked: initialJobDaysLocked }}
                            canEditControls={canEditControls}
                            isFieldVisible={isFieldVisible}
                            onToggle={handleToggleClick}
                            onInitialJobDaysChange={e => setInitialJobDays(e.target.value)}
                        />
                    )}

                    {section.key === 'directory' && (
                        <DirectorySection
                            fields={getFieldsBySection(section.key, isFieldVisible)}
                            showCompanyIds={checkUserAccess('enable_job_updates')}
                            lnAliasesUrns={lnAliasesUrns}
                            onLnAliasesChange={e => setLnAliasesUrns(e.target.value)}
                            canEditControls={canEditControls}
                            values={values}
                            onToggle={handleToggleClick}
                        />
                    )}
                </div>
            ))}
            </>}
        </div>
    )
}

export default ControlsTab
